import { useContext, useMemo, useState } from "react";

// Context
import { CreditsContext } from "context/credits";
import { IterableContext } from "context/iterable/iterable";
import { NotificationContext } from "context/notification/notification";

// Enums
import { IterableEmbeddedMessagesPlacementId } from "enums/iterable/IterableEmbeddedMessagePlacementId";
import { NotificationSeverity } from "enums/notifications/NotificationSeverity";

// Localization
import { useTranslation } from "localization/localization";

// Models
import { Notification } from "models/notification/Notification";

// Platforms
import { SandboxxRestAPI } from "utils/sandboxx";

// Utils
import { getErrorMessage } from "utils/errorUtils";
import { DEV } from "utils/urlUtils";

const embeddedMessagesPlacementId = DEV
    ? IterableEmbeddedMessagesPlacementId.PROFILE_RESOURCES_STAGE
    : IterableEmbeddedMessagesPlacementId.PROFILE_RESOURCES_PROD;

export const useProfileResources = () => {
    /**
     * Custom Hooks
     */

    const { t } = useTranslation();

    /**
     * useContext
     */

    const { fetchCredits } = useContext(CreditsContext);
    const { embeddedMessages, isLoadingEmbeddedMessages } =
        useContext(IterableContext);
    const { addNotification } = useContext(NotificationContext);

    /**
     * useState
     */

    const [isClaimLetterModalOpen, setIsClaimLetterModalOpen] = useState(false);
    const [isClaimingLetter, setIsClaimingLetter] = useState(false);
    const [selectedMessage, setSelectedMessage] = useState(null);
    const [selectedPromotionData, setSelectedPromotionData] = useState(null);

    /**
     * useMemo
     */

    const messages = useMemo(() => {
        if (!embeddedMessages) return null;
        return embeddedMessages[embeddedMessagesPlacementId];
    }, [embeddedMessages]);

    /**
     * End Hooks
     */

    function handleOpenClaimLetterModal(message) {
        setSelectedMessage(message);
        setSelectedPromotionData(message?.payload?.promotion);
        setIsClaimLetterModalOpen(true);
    }

    function handleCloseClaimLetterModal() {
        setIsClaimLetterModalOpen(false);
        setSelectedPromotionData(null);
        setSelectedMessage(null);
    }

    async function handleClaimLetter() {
        const campaignId = selectedMessage?.metadata?.campaignId;

        if (!campaignId) {
            return;
        }

        setIsClaimingLetter(true);
        try {
            const { json } = await SandboxxRestAPI.redeemIterableCreditCampaign(
                {
                    campaignId,
                }
            );
            fetchCredits();
            handleCloseClaimLetterModal();

            const defaultSuccessMessage = t(
                "letter_claim_modal.success_message",
                { ns: "components" }
            );
            const apiMessage = json?.message;
            const notificationMessage = apiMessage || defaultSuccessMessage;

            addNotification(
                new Notification({
                    id: "LETTER_CLAIM_SUCCESS",
                    message: notificationMessage,
                    options: { severity: NotificationSeverity.SUCCESS },
                })
            );
        } catch (err) {
            const errorMessage = getErrorMessage(err);
            addNotification(
                new Notification({
                    id: "FREE_LETTER_CLAIM_ERROR",
                    message: errorMessage,
                    options: { severity: NotificationSeverity.ERROR },
                })
            );
        } finally {
            setIsClaimingLetter(false);
        }
    }

    return {
        handleClaimLetter,
        handleCloseClaimLetterModal,
        handleOpenClaimLetterModal,
        isClaimingLetter,
        isClaimLetterModalOpen,
        isLoadingEmbeddedMessages,
        messages,
        selectedPromotionData,
    };
};
